import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { getLoaAlignment } from '@/api/loaAlignment';
import { formatMoney } from '@/lib/dateFormat';
import type { LoaItem, LoaLimit, LoaStatus } from '@/types/oscar';

const statusLabel: Record<LoaStatus, string> = {
  match: 'Matches',
  'dal-higher': 'DAL above policy',
  'dal-lower': 'DAL below policy',
  'no-dal-row': 'No DAL limit',
};

const statusHint: Record<LoaStatus, string> = {
  match: 'The DAL gives this team the same amount as the policy.',
  'dal-higher': 'The DAL gives this team MORE authority than the policy allows. Look at these first.',
  'dal-lower': 'The DAL gives this team LESS than the policy allows: tighter than policy.',
  'no-dal-row': 'The team has no PO limit for this line type and sub-line type, so no authority, but the policy gives it some.',
};

const statusCls: Record<LoaStatus, string> = {
  match: 'bg-navy/10 text-navy',
  'dal-higher': 'bg-orange text-white',
  'dal-lower': 'bg-orange/15 text-navy',
  'no-dal-row': 'bg-darkGrey/10 text-darkGrey',
};

const comparisonText: Record<LoaItem['Comparison'], string> = {
  compared: 'Compared',
  'not-comparable': 'Not comparable',
  'awaiting-source': 'Awaiting source',
};

type Filter = 'all' | 'differences' | 'dal-higher';

function Badge({ status }: { status: LoaStatus }) {
  return (
    <span title={statusHint[status]} className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${statusCls[status]}`}>
      {statusLabel[status]}
    </span>
  );
}

function Tile({ label, value, hint, onClick, active }: { label: string; value: number; hint: string; onClick: () => void; active: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hint}
      className={`rounded border px-3 py-2 text-left ${active ? 'border-navy bg-navy/5' : 'border-border bg-white hover:bg-bg'}`}
    >
      <div className="text-2xl font-bold text-navy">{value}</div>
      <div className="text-xs font-semibold text-darkGrey">{label}</div>
    </button>
  );
}

function LimitRow({ l }: { l: LoaLimit }) {
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-1.5">
        {l.TeamCode}
        {!l.TeamFound && <span title="Not an active team in Oscar: the mapping needs fixing" className="ml-2 text-xs font-semibold text-orange">team not found</span>}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(l.LoaAmount)}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatMoney(l.DalAmount ?? 0)}
        {l.DalAmount == null && <span className="ml-1 text-xs text-darkGrey/60">none</span>}
      </td>
      <td className="px-3 py-1.5 text-darkGrey/70">{l.Source === 'Exception' ? 'Exceptions column' : 'Matrix'}</td>
      <td className="px-3 py-1.5"><Badge status={l.Status} /></td>
    </tr>
  );
}

export function LoaAlignmentPage() {
  const query = useQuery({ queryKey: ['loa-alignment'], queryFn: ({ signal }) => getLoaAlignment(signal) });
  const [filter, setFilter] = useState<Filter>('all');
  const data = query.data;

  const items = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.Items;
    const keep = (l: LoaLimit) => (filter === 'dal-higher' ? l.Status === 'dal-higher' : l.Status !== 'match');
    return data.Items
      .map((i) => ({ ...i, Limits: i.Limits.filter(keep) }))
      .filter((i) => i.Limits.length > 0);
  }, [data, filter]);

  const differences = data ? data.DalHigher + data.DalLower + data.NoDalRow : 0;

  return (
    <section>
      <header className="mb-5">
        <Link to="/settings" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-darkGrey hover:text-navy">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Settings
        </Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Levels of Authority and PO limits</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The Levels of Authority policy beside the PO limits Baseplan holds for each team (the DAL). Read-only: nothing here changes a limit.
        </p>
      </header>

      <p role="note" className="mb-4 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
        <span>
          This is an AI-assisted reading of {data?.PolicyReference ?? 'the Levels of Authority policy'}, not the policy itself. Only figures read
          without doubt are compared, and each should be confirmed by Finance. The policy is owned by the CFO and can only be amended with the approval
          of the Managing Director, Sime Darby Industrial Solutions. Differences are for Finance to decide on; they are not corrected automatically.
        </span>
      </p>

      {data && (
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
          <Tile label="Figures compared" value={data.Compared} hint="Show everything" onClick={() => setFilter('all')} active={filter === 'all'} />
          <Tile label="Match the policy" value={data.Matching} hint="Same amount in the DAL and the policy" onClick={() => setFilter('all')} active={false} />
          <Tile label="Differ" value={differences} hint="Any figure that is not the same as the policy" onClick={() => setFilter('differences')} active={filter === 'differences'} />
          <Tile label="DAL above policy" value={data.DalHigher} hint={statusHint['dal-higher']} onClick={() => setFilter('dal-higher')} active={filter === 'dal-higher'} />
        </div>
      )}

      {query.isLoading && <p className="py-6 text-center text-darkGrey/60">Loading…</p>}
      {query.isError && <p className="py-6 text-center text-red">Could not load the alignment.</p>}
      {data && items.length === 0 && <p className="py-6 text-center text-darkGrey/60">Nothing differs.</p>}

      <div className="space-y-4">
        {items.map((i) => (
          <article key={i.Code} className="overflow-x-auto rounded border border-border bg-white shadow-sm">
            <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border bg-bg px-3 py-2">
              <h2 className="font-semibold text-navy">{i.Code} · {i.Description}</h2>
              <span className="text-xs text-darkGrey/70">
                {i.LineType ? `Baseplan line ${i.LineType}${i.SubLineType ? ` / ${i.SubLineType}` : ''}` : 'No PO line type'}
                {i.Gpa ? ` · ${i.Gpa}` : ''}
              </span>
              <span className="ml-auto text-xs font-semibold text-darkGrey">{comparisonText[i.Comparison]}</span>
            </header>
            {i.Note && <p className="px-3 pt-2 text-xs text-darkGrey/80">{i.Note}</p>}
            {i.Limits.length > 0 && (
              <table className="mt-1 w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-darkGrey/70">
                  <tr>
                    <th className="px-3 py-1.5">Team</th>
                    <th className="px-3 py-1.5 text-right">Policy</th>
                    <th className="px-3 py-1.5 text-right">DAL</th>
                    <th className="px-3 py-1.5">Policy source</th>
                    <th className="px-3 py-1.5">Result</th>
                  </tr>
                </thead>
                <tbody>{i.Limits.map((l) => <LimitRow key={l.TeamCode} l={l} />)}</tbody>
              </table>
            )}
          </article>
        ))}
      </div>

      <p className="mt-4 text-xs text-darkGrey/70">
        DAL figures are the Purchase Order limits in Oscar (category 2) for active teams, the same ones the nightly 20:00 job sends to Baseplan.
        Baseplan cannot tell a budgeted purchase from an unbudgeted one, or limit a contract&rsquo;s length, so those parts of the policy are not checked here.
      </p>
    </section>
  );
}
